/**
 * Repeatable instance editor — shared form shell for add/edit (#1154).
 * Web: inline in the step (layout="inline"). Native: inside RepeatableInstanceEditModal.
 * Structure mirrors {@link RackEditScreen} (#995).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Platform,
  Dimensions,
  ActivityIndicator,
} from 'react-native';

import { RepeatableInstanceEditChrome } from './RepeatableInstanceEditChrome';
import { REQUEST_FORM_KEYBOARD_BOTTOM_OFFSET } from '../../constants/requestFormLayout';
import { FormKeyboardScrollProvider } from '../../contexts/FormKeyboardScrollContext';
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog';
import { useResponsive } from '../../hooks/useResponsive';
import { colors, spacing } from '../../theme';
import { X } from '../icons';
import { RackEditFormActions } from '../racks/RackEditFormActions';
import { KeyboardAwareFormScrollView } from '../shared/KeyboardAwareFormScrollView';
import { RichTextKeyboardDismissBar } from '../shared/RichTextKeyboardDismissBar';

import type { RepeatableInstance } from './types';

export type RepeatableInstanceEditLayout = 'modal' | 'inline';

const WEB_MODAL_MAX_HEIGHT = Dimensions.get('window').height * 0.9;
const WEB_CONTENT_MAX_HEIGHT = Dimensions.get('window').height * 0.55;
const WEB_INLINE_FOOTER_STICKY_BOTTOM = 72;

const DISCARD_TITLE = 'Discard unsaved changes?';
const DISCARD_MESSAGE = 'You have unsaved changes that will be lost.';
const DISCARD_CONFIRM = 'Discard';
const DISCARD_CANCEL = 'Keep editing';

export interface RepeatableInstanceEditScreenProps<
  T extends RepeatableInstance = RepeatableInstance,
> {
  onClose: () => void;
  onSave: () => Promise<void>;
  onDelete?: () => Promise<void>;
  isSaving?: boolean;
  isDeleting?: boolean;
  disabled?: boolean;
  layout?: RepeatableInstanceEditLayout;
  hideEditorActions?: boolean;
  saveLabel?: string;
  deleteLabel?: string;
  /** Discard-dialog copy overrides (#997 Story 6.3 R5); defaults keep the generic copy. */
  discardTitle?: string;
  discardMessage?: string;
  discardConfirmLabel?: string;
  discardKeepEditingLabel?: string;
  isNew?: boolean;
  hasChanges?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  saveTriggerRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  dismissTriggerRef?: React.MutableRefObject<(() => Promise<boolean>) | null>;
  instancesForNavigation?: T[];
  currentInstanceId?: number;
  instanceTitle?: string;
  backToListLabel?: string;
  newInstanceFallbackTitle?: string;
  entityPrefix?: string;
  onNavigateToInstance?: (instance: T) => void;
  children: React.ReactNode;
  testID?: string;
}

export function RepeatableInstanceEditScreen<
  T extends RepeatableInstance = RepeatableInstance,
>({
  onClose,
  onSave,
  onDelete,
  isSaving = false,
  isDeleting = false,
  disabled = false,
  layout = 'modal',
  hideEditorActions = false,
  saveLabel = 'Save',
  deleteLabel = 'Delete',
  discardTitle = DISCARD_TITLE,
  discardMessage = DISCARD_MESSAGE,
  discardConfirmLabel = DISCARD_CONFIRM,
  discardKeepEditingLabel = DISCARD_CANCEL,
  isNew = false,
  hasChanges: hasChangesProp = false,
  onDirtyChange,
  saveTriggerRef,
  dismissTriggerRef,
  instancesForNavigation = [],
  currentInstanceId,
  instanceTitle,
  backToListLabel = 'All items',
  newInstanceFallbackTitle = 'New record',
  entityPrefix = 'Item',
  onNavigateToInstance,
  children,
  testID = 'repeatable-instance-edit-screen',
}: RepeatableInstanceEditScreenProps<T>): React.ReactElement {
  const isInlineWeb = layout === 'inline' && Platform.OS === 'web';
  const showChrome = Boolean(onNavigateToInstance);
  const formScrollRef = useRef<ScrollView>(null);
  // #1415: the chrome breadcrumb is the only back-to-list affordance, and it was
  // web-only. On native the modal renders chrome without it (and the X fallback is
  // skipped when chrome shows), leaving no way back. Surface it on native too.
  const showBackControl = isInlineWeb || (showChrome && Platform.OS !== 'web');
  const { isSmallScreen } = useResponsive();
  const useStackedActions = Platform.OS !== 'web' || isSmallScreen;
  const showEditorActions = !hideEditorActions;

  const [hasChanges, setHasChanges] = useState(hasChangesProp);
  const { confirm, confirmationDialog } = useConfirmationDialog();

  useEffect(() => {
    setHasChanges(hasChangesProp);
  }, [hasChangesProp]);

  useEffect(() => {
    onDirtyChange?.(hasChanges);
  }, [hasChanges, onDirtyChange]);

  const handleSave = useCallback(async () => {
    await onSave();
    setHasChanges(false);
  }, [onSave]);

  useEffect(() => {
    if (!saveTriggerRef) {
      return;
    }
    saveTriggerRef.current = handleSave;
    return () => {
      saveTriggerRef.current = null;
    };
  }, [handleSave, saveTriggerRef]);

  const confirmDiscardUnsaved = useCallback(async (): Promise<boolean> => {
    if (!hasChanges) {
      return true;
    }
    return confirm({
      title: discardTitle,
      message: discardMessage,
      confirmText: discardConfirmLabel,
      cancelText: discardKeepEditingLabel,
      confirmButtonStyle: 'danger',
      cancelButtonStyle: 'clear',
    });
  }, [
    hasChanges,
    confirm,
    discardTitle,
    discardMessage,
    discardConfirmLabel,
    discardKeepEditingLabel,
  ]);

  const handleClose = useCallback(() => {
    void (async () => {
      const mayClose = await confirmDiscardUnsaved();
      if (mayClose) {
        onClose();
      }
    })();
  }, [confirmDiscardUnsaved, onClose]);

  useEffect(() => {
    if (!dismissTriggerRef) {
      return;
    }
    dismissTriggerRef.current = async () => {
      const mayClose = await confirmDiscardUnsaved();
      if (mayClose) {
        onClose();
      }
      return mayClose;
    };
    return () => {
      dismissTriggerRef.current = null;
    };
  }, [confirmDiscardUnsaved, dismissTriggerRef, onClose]);

  const handleDelete = () => {
    if (!onDelete) {
      return;
    }
    void onDelete();
  };

  const isFormDisabled = disabled || isSaving || isDeleting;
  const canSave = isNew ? !isFormDisabled : !isFormDisabled && hasChanges;
  const actionsVariant = useStackedActions ? 'stacked' : 'toolbar';

  const formActions = showEditorActions ? (
    <RackEditFormActions
      testID={testID}
      variant={actionsVariant}
      isNew={isNew}
      canSave={canSave}
      isSaving={isSaving}
      isDeleting={isDeleting}
      isFormDisabled={isFormDisabled}
      onSave={() => void handleSave()}
      onCancel={handleClose}
      onDelete={onDelete ? handleDelete : undefined}
      saveLabel={saveLabel}
      deleteLabel={deleteLabel}
    />
  ) : null;

  const screen = (
    <>
      <View
        testID={testID}
        style={[
          styles.root,
          isInlineWeb && styles.rootInline,
          showChrome && styles.rootWithChrome,
        ]}
        {...(isInlineWeb
          ? ({ dataSet: { overlayScrollExempt: 'true' } } as const)
          : {})}
      >
        {showChrome ? (
          <RepeatableInstanceEditChrome
            isNew={isNew}
            instancesForNavigation={instancesForNavigation}
            currentInstanceId={currentInstanceId}
            instanceTitle={instanceTitle}
            backToListLabel={backToListLabel}
            newInstanceFallbackTitle={newInstanceFallbackTitle}
            entityPrefix={entityPrefix}
            onBackToList={handleClose}
            onNavigateToInstance={onNavigateToInstance!}
            onConfirmDiscard={confirmDiscardUnsaved}
            showBreadcrumb={showBackControl}
            testID={`${testID}-chrome`}
          />
        ) : (
          <View style={styles.header}>
            <View style={styles.headerTextBlock}>
              <Text style={styles.headerTitle}>
                {instanceTitle?.trim() ||
                  (isNew ? newInstanceFallbackTitle : 'Edit record')}
              </Text>
            </View>
            {!isInlineWeb ? (
              <TouchableOpacity
                testID={`${testID}-close-button`}
                style={styles.closeButton}
                onPress={handleClose}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                accessibilityLabel="Close"
                accessibilityRole="button"
              >
                <X size={18} color={colors.textSecondary} />
              </TouchableOpacity>
            ) : null}
          </View>
        )}

        {isInlineWeb ? (
          <View
            style={[
              styles.content,
              styles.contentInline,
              useStackedActions && styles.contentInlineStacked,
            ]}
            testID={`${testID}-form-scroll`}
          >
            {children}
            {useStackedActions && showEditorActions ? formActions : null}
          </View>
        ) : (
          <KeyboardAwareFormScrollView
            ref={formScrollRef}
            style={styles.content}
            contentContainerStyle={styles.contentContainer}
            showsVerticalScrollIndicator
            nestedScrollEnabled
            bottomOffset={REQUEST_FORM_KEYBOARD_BOTTOM_OFFSET}
            testID={`${testID}-form-scroll`}
          >
            {children}
            {useStackedActions && showEditorActions ? formActions : null}
          </KeyboardAwareFormScrollView>
        )}

        {!useStackedActions && showEditorActions ? (
          <View style={[styles.footer, isInlineWeb && styles.footerInlineWeb]}>
            {formActions}
          </View>
        ) : null}

        {(isSaving || isDeleting) && (
          <View style={styles.savingOverlay} pointerEvents="none">
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        )}
        {!isInlineWeb ? <RichTextKeyboardDismissBar /> : null}
      </View>
      {confirmationDialog}
    </>
  );

  if (isInlineWeb) {
    return screen;
  }

  return (
    <FormKeyboardScrollProvider
      scrollRef={formScrollRef}
      bottomOffset={REQUEST_FORM_KEYBOARD_BOTTOM_OFFSET}
      isolateChrome
    >
      {screen}
    </FormKeyboardScrollProvider>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    backgroundColor: colors.backgroundPrimary,
    ...Platform.select({
      web: {
        maxHeight: WEB_MODAL_MAX_HEIGHT,
      },
    }),
  },
  rootInline: {
    width: '100%' as unknown as number,
    maxWidth: '100%' as unknown as number,
    maxHeight: 'none' as unknown as number,
    borderWidth: 0,
    overflow: 'visible' as unknown as undefined,
  },
  rootWithChrome: {
    backgroundColor: 'transparent',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderCard,
    flexShrink: 0,
    gap: spacing.sm,
  },
  headerTextBlock: {
    flex: 1,
    paddingRight: spacing.md,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.borderCard,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.backgroundPrimary,
  },
  content: {
    flexGrow: 1,
    flexShrink: 1,
    ...Platform.select({
      web: {
        minHeight: 0,
        maxHeight: WEB_CONTENT_MAX_HEIGHT,
        overflow: 'auto' as unknown as undefined,
      },
      default: {
        flex: 1,
      },
    }),
  },
  contentInline: {
    maxHeight: 'none' as unknown as number,
    flexGrow: 0,
    flexShrink: 0,
    padding: spacing.lg,
    paddingBottom: spacing.xl + 48,
  },
  contentInlineStacked: {
    paddingBottom: spacing.xl,
  },
  contentContainer: {
    padding: spacing.lg,
    paddingBottom: spacing.xl,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.borderCard,
    gap: spacing.md,
    flexShrink: 0,
  },
  footerInlineWeb: {
    ...Platform.select({
      web: {
        position: 'sticky' as unknown as undefined,
        bottom: WEB_INLINE_FOOTER_STICKY_BOTTOM,
        zIndex: 900,
        backgroundColor: colors.backgroundPrimary,
        marginTop: spacing.sm,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.08,
        shadowRadius: 4,
      },
    }),
  },
  savingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(255,255,255,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
